import { useState } from 'react';
import { Button } from '../components/ui/button';
import { useGameInfo } from '../hooks/useApi';
import { useTenant } from '../contexts/TenantContext';
import TenantLogo from '../components/TenantLogo';
import { 
  TrendingUp, 
  Trophy, 
  Zap, 
  Shield, 
  Target,
  ChevronRight,
  DollarSign,
  BarChart3,
  Users,
  Briefcase
} from 'lucide-react';

const LandingPage = ({ onGetStarted }) => {
  const { gameInfo } = useGameInfo();
  const { branding, settings, isWhiteLabel } = useTenant();

  const features = [
    {
      icon: Briefcase,
      title: 'Build Your Portfolio',
      description: 'Select 10 instruments from stocks, crypto, commodities, and ETFs with your V$100,000 budget.'
    },
    {
      icon: Zap,
      title: 'Power Play',
      description: 'Choose one instrument each week to score 3x points - for gains AND losses. High risk, high reward!'
    },
    {
      icon: Trophy,
      title: 'Weekly Competition',
      description: 'Compete on global leaderboards every week. Earn points for price gains, dividends, and stability.'
    },
    {
      icon: Shield,
      title: 'Strategic Chips',
      description: 'Use Wildcard, Stop-Loss Shield, and other chips strategically throughout the season.'
    }
  ];

  const stats = [
    { value: 'V$100K', label: 'Starting Budget' },
    { value: '10', label: 'Portfolio Slots' },
    { value: '3x', label: 'Power Play Multiplier' },
    { value: '13', label: 'Weeks per Season' }
  ];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient - uses tenant primary color */}
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom right, rgba(var(--tenant-primary-rgb), 0.1), transparent, rgba(99, 102, 241, 0.1))`
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at top, rgba(var(--tenant-primary-rgb), 0.1), transparent, transparent)`
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32">
          <div className="text-center">
            {/* Logo - Tenant aware */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <TenantLogo size="lg" showText={false} />
              <h1 className="text-5xl sm:text-6xl font-bold text-white tracking-tight">
                {branding?.company_name || 'Trader Titans'}
              </h1>
            </div>

            {/* Tagline - Tenant aware */}
            <p className="text-xl sm:text-2xl text-slate-300 max-w-3xl mx-auto mb-4">
              {branding?.tagline || 'The Fantasy Trading Game for Financial Markets'}
            </p>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
              Build virtual portfolios. Compete for points. Climb the leaderboards. 
              No real money at risk.
            </p>

            {/* CTA - Uses tenant primary color */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                onClick={onGetStarted}
                className="text-lg px-8 py-6 text-white"
                style={{ backgroundColor: branding?.primary_color || '#10B981' }}
                data-testid="hero-cta-btn"
              >
                Start Playing Free
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <a 
                href="/GAMEPLAY_DESIGN_DOCUMENT.html" 
                target="_blank"
                className="text-slate-400 hover:text-emerald-400 flex items-center gap-2 transition-colors"
                style={{ '--tw-text-opacity': 1 }}
                onMouseEnter={(e) => e.target.style.color = branding?.primary_color || '#10B981'}
                onMouseLeave={(e) => e.target.style.color = ''}
              >
                Learn the Rules
                <ChevronRight className="h-4 w-4" />
              </a>
            </div>

            {/* Season info */}
            {gameInfo && (
              <div className="mt-10 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700">
                <span className="text-emerald-400 font-semibold">Season {gameInfo.current_season}</span>
                <span className="text-slate-500">|</span>
                <span className="text-slate-400">Week {gameInfo.current_week}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative -mt-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <div 
                key={i}
                className="bg-slate-900/80 backdrop-blur-sm border border-slate-700 rounded-xl p-6 text-center"
              >
                <div className="text-3xl font-bold text-emerald-400">{stat.value}</div>
                <div className="text-slate-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Fantasy sports meets financial markets - compete for points, not profits
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-emerald-500/50 transition-colors"
              >
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Points System Preview */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Points, Not Just Profits
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Strategy matters more than luck. Earn points for smart decisions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-slate-800/50 rounded-xl p-6">
              <div className="flex items-center gap-2 text-emerald-400 mb-4">
                <TrendingUp className="h-5 w-5" />
                <span className="font-semibold">Price Gains</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">+1% to +3%</span>
                  <span className="text-emerald-400">+2 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">+5% to +10%</span>
                  <span className="text-emerald-400">+7 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">+20%+</span>
                  <span className="text-emerald-400">+20 pts</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6">
              <div className="flex items-center gap-2 text-amber-400 mb-4">
                <Target className="h-5 w-5" />
                <span className="font-semibold">Bonuses</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Dividend capture</span>
                  <span className="text-amber-400">+3 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Stability bonus</span>
                  <span className="text-amber-400">+3 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Recovery bonus</span>
                  <span className="text-amber-400">+4-8 pts</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6">
              <div className="flex items-center gap-2 text-red-400 mb-4">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">Penalties</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Extra trade</span>
                  <span className="text-red-400">-10 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Flash crash</span>
                  <span className="text-red-400">-3 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">No diversity</span>
                  <span className="text-red-400">-5 pts</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Become a Titan?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Join thousands of players competing in the fantasy trading arena. 
            Free to play, no real money at risk.
          </p>
          <Button 
            size="lg"
            onClick={onGetStarted}
            className="bg-emerald-600 hover:bg-emerald-700 text-lg px-10 py-6"
            data-testid="bottom-cta-btn"
          >
            Create Your Portfolio
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-400">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
              <span>Trader Titans</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <a href="/GAMEPLAY_DESIGN_DOCUMENT.html" target="_blank" className="hover:text-emerald-400">Game Rules</a>
              <a href="/HOW_TO_WIN_STRATEGY_GUIDE.html" target="_blank" className="hover:text-emerald-400">Strategy Guide</a>
              <a href="/sponsorship" className="hover:text-emerald-400" data-testid="footer-sponsorship-link">Sponsorship</a>
              <a href="mailto:support@tradertitans.com" className="hover:text-emerald-400" data-testid="footer-contact-link">Contact Us</a>
            </div>
            <p className="text-slate-500 text-sm">
              For entertainment only. No real money at risk.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
